import Vue from 'vue';
import Component from 'vue-class-component';
import { ApiUser, getUser, isStoreAuthenticated } from '@/api/user';
import { Mutation, State } from 'vuex-class';
import { Route } from 'vue-router';
import { StoreType } from '@extTypes/dto/user';
import { BDropdown } from 'bootstrap-vue';
import { Watch } from 'vue-property-decorator';
import { isWooCommerceStore } from '@/utils/stores';

@Component({
    name: 'App'
})
export default class App extends Vue {
    @State('user') user!: ApiUser | null;
    @State('store') store!: string;
    @Mutation('setStore') setStore!: (store: string | null) => void;

    @Watch('store')
    onStoreChange() {
        this.checkStoreAuthentication();
    }

    showStoreSelector = true;
    allowAllStoresSelector = false;
    storeAuthenticated = true;
    isOpenRoute = false;

    $refs!: {
        storesDropdown: BDropdown;
        appInstallAnchor: HTMLAnchorElement;
    };

    get storeSelectorTitle(): string {
        if (!this.store) {
            return 'All stores';
        } else {
            return this.store;
        }
    }

    get appInstallUrl(): string {
        if (
            this.store &&
            this.user &&
            this.user.customilyUser &&
            isWooCommerceStore(this.store, this.user.customilyUser.Stores)
        ) {
            return `https://${this.store}/wp-login.php`;
        }
        return `https://sh.customily.com/shopify/auth?shop=${this.store}`;
    }

    setStoreUrl(store: string | null) {
        this.setStore(store);
    }

    showAuthError() {
        // Remove any loading element
        document.querySelectorAll('.loading-container')[0]?.remove();
        this.storeAuthenticated = false;
    }

    onStoresDropdownClick() {
        this.$refs.storesDropdown.show();
    }

    goToAppInstallLink() {
        this.$refs.appInstallAnchor.click();
    }

    async checkStoreAuthentication() {
        if (this.store) {
            try {
                const store = await isStoreAuthenticated(this.store);
                if (!store.authenticated) {
                    this.storeAuthenticated = false;
                } else {
                    this.storeAuthenticated = true;
                }
            } catch (error) {
                if (error.request.status !== 401) {
                    window.captureException(error);
                }
                this.storeAuthenticated = false;
            }
        } else {
            this.storeAuthenticated = true;
        }
    }

    checkStoreSelectorVisibility(path: string) {
        const selectorPaths = ['/products', '/settings', '/'];
        const allowAllStoresPaths = ['/sets'];

        this.showStoreSelector = selectorPaths.includes(path);
        this.allowAllStoresSelector = allowAllStoresPaths.includes(path);

        // On routes that should not be able to select 'All Stores' option, select the first available store
        //  if the current one is null
        if (!this.allowAllStoresSelector && !this.store && this.user) {
            const url = this.user.customilyUser.Stores.find(
                (s) => s.Type === StoreType.Shopify || s.Type === StoreType.WooCommerce
            )?.Url;
            if (url) {
                this.setStore(url);
            } else {
                console.error('User does not have any Shopify stores!');
            }
        }
    }

    async created() {
        this.isOpenRoute = this.$route.meta.isOpen;

        this.$router.beforeEach((to: Route, from: Route, next) => {
            this.isOpenRoute = this.$route.meta.isOpen;
            this.checkStoreSelectorVisibility(to.path);
            next();
        });
        this.checkStoreSelectorVisibility(this.$router.currentRoute.path);

        document.addEventListener('auth-error', () => {
            this.storeAuthenticated = false;
        });

        if (!this.isOpenRoute) {
            try {
                await getUser();
            } catch {
                this.storeAuthenticated = false;
            }
            this.checkStoreAuthentication();
        }
    }
}
